import { useState, useEffect } from 'react';
import PageWithNavbar from '../components/PageWithNavbar';
import { API_BASE_URL } from '../lib/constants';
import styles from './recipes.module.css';

interface Recipe {
  id: number;
  title: string;
  description: string;
  servings: number;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  estimated_cost_per_serving?: string;
  estimated_calories_per_serving?: number;
  is_favorite: boolean;
  source_url?: string;
  source_name?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
  has_modifications: boolean;
}

export default function Recipes() {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [multipleIngredientSearch, setMultipleIngredientSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any>(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showAddRecipeModal, setShowAddRecipeModal] = useState(false);
  const [newRecipeUrl, setNewRecipeUrl] = useState('');
  const [extractingRecipe, setExtractingRecipe] = useState(false);
  const token = localStorage.getItem('token');

  const fetchRecipes = async () => {
    try {
      const url = showFavoritesOnly 
        ? `${API_BASE_URL}/api/recipes/favorites/`
        : `${API_BASE_URL}/api/recipes/`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipes(data);
      }
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchRecipes = async (query: string = searchQuery, ingredient: string = ingredientSearch, ingredients: string = multipleIngredientSearch) => {
    // If no search parameters, fetch all recipes
    if (!query.trim() && !ingredient.trim() && !ingredients.trim()) {
      fetchRecipes();
      setSearchResults(null);
      return;
    }
    
    try {
      // Build search URL with parameters
      const params = new URLSearchParams();
      if (query.trim()) params.append('q', query);
      if (ingredient.trim()) params.append('ingredient', ingredient);
      if (ingredients.trim()) params.append('ingredients', ingredients);
      
      const response = await fetch(`${API_BASE_URL}/api/recipes/search/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle new API response format
        if (data.results && data.search_info) {
          setRecipes(data.results);
          setSearchResults(data.search_info);
        } else {
          // Fallback for old response format
          setRecipes(data);
          setSearchResults(null);
        }
      }
    } catch (error) {
      console.error('Error searching recipes:', error);
    }
  };

  const clearAllSearches = () => {
    setSearchQuery('');
    setIngredientSearch('');
    setMultipleIngredientSearch('');
    setSearchResults(null);
    fetchRecipes();
  };

  const toggleFavorite = async (recipeId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipeId}/toggle-favorite/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipes(recipes.map(recipe => 
          recipe.id === recipeId 
            ? { ...recipe, is_favorite: data.is_favorite }
            : recipe
        ));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const addRecipeFromUrl = async () => {
    if (!newRecipeUrl.trim()) return;
    
    setExtractingRecipe(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/from-url/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: newRecipeUrl,
          extract_recipe: true
        }),
      });
      
      if (response.ok) {
        const newRecipe = await response.json();
        setRecipes([newRecipe, ...recipes]);
        setNewRecipeUrl('');
        setShowAddRecipeModal(false);
      } else {
        alert('Failed to extract recipe from URL. Please try again.');
      }
    } catch (error) {
      console.error('Error adding recipe from URL:', error);
      alert('Error adding recipe. Please try again.');
    } finally {
      setExtractingRecipe(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, [showFavoritesOnly]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchRecipes();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, ingredientSearch, multipleIngredientSearch]);

  const formatTime = (minutes?: number) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <PageWithNavbar>
        <section className={styles.container}>
          <div className={styles.loadingContainer}>
            <p>Loading recipes...</p>
          </div>
        </section>
      </PageWithNavbar>
    );
  }

  return (
    <PageWithNavbar>
      <section className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>My Cookbook</h1>
          <button
            onClick={() => setShowAddRecipeModal(true)}
            className={styles.addButton}
          >
            + Add Recipe
          </button>
        </div>

        {/* Search and Filters */}
        <div className={styles.filtersContainer}>
          <div className={styles.filtersContent}>
            <div className={styles.searchSection}>
              <input
                type="text"
                placeholder="Search recipes by title, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              
              <input
                type="text"
                placeholder="Search by single ingredient (e.g., 'chicken')"
                value={ingredientSearch}
                onChange={(e) => setIngredientSearch(e.target.value)}
                className={styles.searchInput}
              />
              
              <input
                type="text"
                placeholder="Search by multiple ingredients (e.g., 'chicken, rice, onion')"
                value={multipleIngredientSearch}
                onChange={(e) => setMultipleIngredientSearch(e.target.value)}
                className={styles.searchInput}
              />
              
              {(searchQuery || ingredientSearch || multipleIngredientSearch) && (
                <button
                  onClick={clearAllSearches}
                  className={styles.clearSearchButton}
                >
                  Clear All Searches
                </button>
              )}
            </div>
            
            <div className={styles.filtersRow}>
              <label className={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={showFavoritesOnly}
                  onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                />
                Favorites only
              </label>
            </div>
          </div>
          
          {/* Search Results Info */}
          {searchResults && (
            <div className={styles.searchResultsInfo}>
              <p>
                Found {searchResults.total_results} recipe{searchResults.total_results !== 1 ? 's' : ''}
                {searchResults.query && ` matching "${searchResults.query}"`}
                {searchResults.ingredient && ` containing "${searchResults.ingredient}"`}
                {searchResults.ingredients && ` containing all: "${searchResults.ingredients}"`}
              </p>
            </div>
          )}
        </div>

        {/* Recipe Grid */}
        <div className={styles.recipeGrid}>
          {Array.isArray(recipes) && recipes.map((recipe) => (
            <div
              key={recipe.id}
              className={styles.recipeCard}
            >
              {/* Recipe Header */}
              <div className={styles.recipeHeader}>
                <a
                  href={`/recipe/${recipe.id}`}
                  className={styles.recipeTitle}
                >
                  <h3 className={styles.recipeTitleHeading}>
                    {recipe.title}
                  </h3>
                </a>
                <button
                  onClick={() => toggleFavorite(recipe.id)}
                  className={`${styles.favoriteButton} ${recipe.is_favorite ? styles.favorited : styles.unfavorited}`}
                  title={recipe.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {recipe.is_favorite ? '❤️' : '🤍'}
                </button>
              </div>

              {/* Recipe Description */}
              {recipe.description && (
                <p className={styles.recipeDescription}>
                  {recipe.description}
                </p>
              )}

              {/* Recipe Meta */}
              <div className={styles.recipeMeta}>
                <div className={`${styles.metaTag} ${styles.metaTagDefault}`}>
                  🍽️ {recipe.servings} serving{recipe.servings !== 1 ? 's' : ''}
                </div>
                
                {recipe.total_time_minutes && (
                  <div className={`${styles.metaTag} ${styles.metaTagDefault}`}>
                    ⏱️ {formatTime(recipe.total_time_minutes)}
                  </div>
                )}
                
                {recipe.estimated_cost_per_serving && (
                  <div className={`${styles.metaTag} ${styles.metaTagCost}`}>
                    💰 ${recipe.estimated_cost_per_serving}/serving
                  </div>
                )}
                
                {recipe.estimated_calories_per_serving && (
                  <div className={`${styles.metaTag} ${styles.metaTagCalories}`}>
                    🔥 {recipe.estimated_calories_per_serving} cal
                  </div>
                )}
              </div>

              {/* Source and Modifications */}
              <div className={styles.recipeSource}>
                {recipe.source_url && (
                  <div className={styles.sourceText}>
                    Source: {recipe.source_name || 'Web'}
                  </div>
                )}
                
                {recipe.has_modifications && (
                  <div className={styles.modificationTag}>
                    ✏️ Modified
                  </div>
                )}
              </div>

              {/* Actions */}
              {recipe.source_url && (
                <div className={styles.actionsContainer}>
                  <button
                    onClick={() => window.open(recipe.source_url, '_blank')}
                    className={styles.sourceButton}
                    title="View original recipe"
                  >
                    🔗 Original Recipe
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {Array.isArray(recipes) && recipes.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📖</div>
            <h3 className={styles.emptyStateTitle}>
              {searchQuery ? 'No recipes found' : 'Your cookbook is empty'}
            </h3>
            <p className={styles.emptyStateText}>
              {searchQuery 
                ? 'Try adjusting your search terms or filters'
                : 'Start building your recipe collection by adding your first recipe!'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowAddRecipeModal(true)}
                className={styles.emptyStateButton}
              >
                Add Your First Recipe
              </button>
            )}
          </div>
        )}

        {/* Add Recipe Modal */}
        {showAddRecipeModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h2 className={styles.modalTitle}>Add Recipe from URL</h2>
              
              <div className={styles.modalField}>
                <label className={styles.modalLabel}>
                  Recipe URL
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/recipe"
                  value={newRecipeUrl}
                  onChange={(e) => setNewRecipeUrl(e.target.value)}
                  className={styles.modalInput}
                />
                <p className={styles.modalHelp}>
                  Paste a URL from any recipe website and we'll extract the ingredients and instructions automatically.
                </p>
              </div>

              <div className={styles.modalActions}>
                <button
                  onClick={() => {
                    setShowAddRecipeModal(false);
                    setNewRecipeUrl('');
                  }}
                  className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
                  disabled={extractingRecipe}
                >
                  Cancel
                </button>
                <button
                  onClick={addRecipeFromUrl}
                  disabled={!newRecipeUrl.trim() || extractingRecipe}
                  className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                >
                  {extractingRecipe ? 'Extracting...' : 'Add Recipe'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </PageWithNavbar>
  );
}